import { useState } from "react";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useQuery, useMutation } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useCustomer } from "@/lib/customer-context";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CreditCard,
  Lock,
  Unlock,
  Plus,
  AlertCircle,
  TrendingUp,
  Clock,
} from "lucide-react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { CardVisual } from "@/components/customer/card-visual";
import { getVariantFromIndex, type CardVariant } from "@/lib/card-designs";
import { SpendingChart } from "@/components/customer/spending-chart";
import { pageTransition, staggerContainer, staggerItem } from "@/lib/animations";
import { RequestCardDialog } from "@/components/customer/request-card-dialog";

interface VirtualCard {
  id: string;
  lastFour: string | null;
  status: string;
  cardType: string;
  cardBrand: string;
  nickname: string | null;
  expMonth: number | null;
  expYear: number | null;
  dailyLimitCents: number | null;
  monthlyLimitCents: number | null;
  singleTxnLimitCents: number | null;
  walletId: string | null;
  cardDesign: number | null;
}

interface Wallet {
  id: string;
  balanceCents: number;
}

export default function PortalCards() {
  const { toast } = useToast();
  const { customer } = useCustomer();
  const { formatCurrency } = useOrgSettings();
  const [requestCardOpen, setRequestCardOpen] = useState(false);

  const { data: cards = [], isLoading } = useQuery<VirtualCard[]>({
    queryKey: ["customer-cards"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/customer/cards");
      if (!response.ok) throw new Error("Failed to fetch cards");
      return response.json();
    },
  });

  // Fetch wallet balance for displaying on card back
  const { data: wallet } = useQuery<Wallet>({
    queryKey: ["customer-wallet"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/customer/wallet");
      if (!response.ok) throw new Error("Failed to fetch wallet");
      return response.json();
    },
  });

  const freezeMutation = useMutation({
    mutationFn: async (cardId: string) => {
      const response = await apiRequest("POST", `/api/customer/cards/${cardId}/freeze`);
      if (!response.ok) throw new Error("Failed to freeze card");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-cards"] });
      toast({ title: "Success", description: "Card frozen successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const unfreezeMutation = useMutation({
    mutationFn: async (cardId: string) => {
      const response = await apiRequest("POST", `/api/customer/cards/${cardId}/unfreeze`);
      if (!response.ok) throw new Error("Failed to unfreeze card");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-cards"] });
      toast({ title: "Success", description: "Card unfrozen successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const activeCards = cards.filter(c => c.status === "ACTIVE").length;
  const pendingCards = cards.filter(c => c.status === "PENDING_APPROVAL").length;

  return (
    <motion.div
      variants={pageTransition}
      initial="initial"
      animate="animate"
      exit="exit"
      className="p-4 md:p-6 space-y-6 md:space-y-8 max-w-7xl mx-auto"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">My Cards</h1>
          <p className="text-muted-foreground mt-1">
            Manage your virtual cards • {activeCards} active{pendingCards > 0 && ` • ${pendingCards} pending`}
          </p>
        </div>
        <Button
          size="lg"
          className="bg-gradient-to-r from-primary to-primary/80"
          onClick={() => setRequestCardOpen(true)}
        >
          <Plus className="h-5 w-5 mr-2" />
          Request Card
        </Button>
      </motion.div>

      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="h-[400px] animate-pulse" />
          ))}
        </div>
      ) : cards.length > 0 ? (
        <>
          {/* Cards Grid */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {cards.map((card, index) => (
              <motion.div key={card.id} variants={staggerItem}>
                <Card className={`overflow-hidden border-2 transition-all ${card.status === "PENDING_APPROVAL" ? "border-amber-300 bg-amber-50/30" : "hover:border-primary/50"}`}>
                  {/* Pending Approval Banner */}
                  {card.status === "PENDING_APPROVAL" && (
                    <div className="bg-amber-100 border-b border-amber-200 px-4 py-2.5 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-600 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Pending Approval</p>
                        <p className="text-xs text-amber-600">Your card request is being reviewed. You'll be notified once approved.</p>
                      </div>
                    </div>
                  )}
                  <CardContent className="p-6 space-y-4">
                    {/* Card Visual with flip animation */}
                    <div className={card.status === "PENDING_APPROVAL" ? "opacity-50 pointer-events-none" : ""}>
                      <CardVisual
                        cardNumber={card.status === "PENDING_APPROVAL" ? "----" : (card.lastFour || "****")}
                        cardHolder={`${customer?.firstName || ""} ${customer?.lastName || ""}`.trim()}
                        expMonth={card.expMonth || undefined}
                        expYear={card.expYear || undefined}
                        status={card.status}
                        nickname={card.nickname || undefined}
                        cardType={card.cardBrand || card.cardType}
                        variant={(card.cardDesign as CardVariant) || getVariantFromIndex(index)}
                        size="sm"
                        balance={wallet?.balanceCents}
                      />
                    </div>

                    {/* Card Info */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">
                          {card.nickname || "Virtual Card"}
                        </h3>
                        <Badge
                          variant={card.status === "ACTIVE" ? "default" : "secondary"}
                          className={card.status === "PENDING_APPROVAL" ? "bg-amber-100 text-amber-700 border-amber-300" : ""}
                        >
                          {card.status === "PENDING_APPROVAL" ? "Pending Approval" : card.status}
                        </Badge>
                      </div>

                      {/* Spending Limit */}
                      {card.monthlyLimitCents && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Monthly Limit</span>
                            <span className="font-medium">
                              {formatCurrency(0)} / {formatCurrency(card.monthlyLimitCents)}
                            </span>
                          </div>
                          <Progress value={0} className="h-2" />
                        </div>
                      )}

                      {/* Action Buttons */}
                      {card.status === "PENDING_APPROVAL" ? (
                        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-center">
                          <p className="text-xs text-amber-700">Card actions will be available once your request is approved.</p>
                        </div>
                      ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <Link href={`/portal/cards/${card.id}`}>
                          <Button variant="outline" className="w-full" size="sm">
                            <TrendingUp className="h-4 w-4 mr-2" />
                            Details
                          </Button>
                        </Link>
                        {card.status === "ACTIVE" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => freezeMutation.mutate(card.id)}
                            disabled={freezeMutation.isPending}
                          >
                            <Lock className="h-4 w-4 mr-2" />
                            Freeze
                          </Button>
                        ) : card.status === "FROZEN" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => unfreezeMutation.mutate(card.id)}
                            disabled={unfreezeMutation.isPending}
                          >
                            <Unlock className="h-4 w-4 mr-2" />
                            Unfreeze
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" disabled>
                            <AlertCircle className="h-4 w-4 mr-2" />
                            {card.status}
                          </Button>
                        )}
                      </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Optional: Spending Distribution Chart */}
          {cards.length > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <SpendingChart
                title="Spending by Card"
                description="Distribution of spending across your cards this month"
                data={cards.map((card, index) => ({
                  category: card.nickname || `Card ${card.lastFour}`,
                  amount: 0, // Would need actual transaction data
                  percentage: 0,
                  color: index % 2 === 0 ? "hsl(var(--chart-1))" : "hsl(var(--chart-2))",
                }))}
                type="pie"
                height={300}
                showLegend
              />
            </motion.div>
          )}
        </>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-2 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <CreditCard className="h-16 w-16 text-muted-foreground mb-4" />
              <h2 className="text-2xl font-bold mb-2">No Cards Yet</h2>
              <p className="text-muted-foreground text-center mb-6 max-w-md">
                Request your first virtual card to start making secure online payments
              </p>
              <Button
                size="lg"
                className="bg-gradient-to-r from-primary to-primary/80"
                onClick={() => setRequestCardOpen(true)}
              >
                <Plus className="h-5 w-5 mr-2" />
                Request Your First Card
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Request Card Dialog */}
      <RequestCardDialog open={requestCardOpen} onOpenChange={setRequestCardOpen} />
    </motion.div>
  );
}
